/**
 * Squads in the match state (R11 step 2, lane D's part; ARCHITECTURE §5.5 "lane D's answer"). A
 * squad is a fixed set of unit ids on one team with a **base** order — its standing posture, which
 * survives a member's death and respawn — and **tasks**: one-shots for the squad or some of its units
 * that override the base order until they expire. Orders arrive as `order` inputs through
 * `Match.apply`, and membership is resolved when the input applies, so a replay never depends on a
 * host's own list. Pure.
 */
import type { GameState, MatchInputBody, MatchSquad, OrderIssuer, PlayerState, SkillTag, SquadOrder, SquadTask } from '../types';
import { callInBlocked, gunFor, CALL_IN_SPREAD } from './callIn';
import { validateOrderForm, orderFormLapsed, teamKnowsUnit, seatedFor, dismountSupersedes } from './orderForms';
import { separatedAt } from './squadMembership';
import { buildOn, buildsLeft, buildRefusal, frontSiteKind, spawnSiteRefusal } from './build';
import { markConfidence } from './marks';
import { BELIEF_FLOOR } from './picture';

/** A team's units in squads of `size`, by unit id (deterministic). */
export function squadsForTeam(players: readonly PlayerState[], team: number, size: number, firstId = 0): MatchSquad[] {
  const ids = players.filter(p => p.teamIndex === team).map(p => p.id).sort((a, b) => a - b);
  const out: MatchSquad[] = [];
  const step = Math.max(1, Math.floor(size) || 1);   // (a size under one makes squads of one — it used to loop forever)
  for (let i = 0; i < ids.length; i += step) out.push({ id: firstId + out.length, team, unitIds: ids.slice(i, i + step), base: null, tasks: [] });
  return out;
}

/** Squads from explicit groups of unit indices within each team's id order (the sector's own squads). */
export function squadsFromGroups(players: readonly PlayerState[], groupsByTeam: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>> | undefined>): MatchSquad[] {
  const out: MatchSquad[] = [];
  groupsByTeam.forEach((groups, team) => {
    if (!groups) return;
    const ids = players.filter(p => p.teamIndex === team).map(p => p.id).sort((a, b) => a - b);
    for (const g of groups) {
      const unitIds = g.map(i => ids[i]).filter((x): x is number => x !== undefined);
      if (unitIds.length) out.push({ id: out.length, team, unitIds, base: null, tasks: [] });
    }
  });
  return out;
}

/** The squad a unit belongs to, or null. */
export function squadOf(state: Pick<GameState, 'squads'>, unitId: number): MatchSquad | null {
  return state.squads?.find(s => s.unitIds.includes(unitId)) ?? null;
}

/** The state the order readers take: the squads, and — when given — the players and structures, so a lapsed 68.0 form (a dead target, a gone seat) is skipped. */
export type OrderReadState = Pick<GameState, 'squads'> & Partial<Pick<GameState, 'players' | 'structures' | 'squadApart'>>;

/**
 * The order in force for a unit at `tick`: its newest unexpired task that names it (or its whole
 * squad), else its squad's base order, else null (no commander: the bot picks for itself).
 */
export function squadOrderFor(state: OrderReadState, unitId: number, tick: number): SquadOrder | null {
  if (separatedAt(state, unitId, tick)) return null;   // 68.10: a separated member fights alone (no squad order reaches it)
  return squadTaskFor(state, unitId, tick)?.order ?? squadOf(state, unitId)?.base ?? null;
}

/** 68.27: a squad marked released, or not. */
function withReleased(sq: MatchSquad, released: boolean): MatchSquad {
  const out = { ...sq };
  if (released) out.released = true; else delete out.released;
  delete out.scouted;   // 69.2e: a new order ends the scout's report too
  return out;
}

/** 68.23: a squad with its base order's giver set (`by` absent: a human's). */
function withBaseBy(sq: MatchSquad, by: OrderIssuer | undefined): MatchSquad {
  const out = { ...sq };
  if (by === 'human' || by === 'commander') out.baseBy = by; else delete out.baseBy;
  return out;
}

/**
 * 69.72 step 5 (DECISIONS 304): a task's rank — the lower wins; within a rank, the newer. 0–1 the human (a member's direct
 * order over the squad's), 2 being lifted (a pickup or rescue hold), 3 a unit's named errand (a convoy leg, a build, a
 * beacon, a fire mission, a gun's crew), 4 a play, 5 the story's objectives (the carry, then the objective's strike, then
 * the HQ's, then 69.79's search for the base — its searcher, which the replan's base order used to clear), 6 the
 * commander's routine. The squad leader's step (7), the posture — the base order, what a lapse falls back
 * to (8) — and a unit's own fire and self-preservation (9, at any rank) are the brain's, under every task.
 */
/** 415 step 3: the ranks the commander tests by name (taskRank's own numbers): a play's, and the commander's routine (free). */
export const TASK_RANK = { play: 4, objectives: 5, routine: 6 } as const;

export function taskRank(t: Pick<SquadTask, 'order' | 'units' | 'by' | 'claim' | 'play'>): number {
  if (t.by === 'human') return t.units ? 0 : 1;
  if (t.claim === 'lift') return 2;
  if (t.units && errandOrder(t.order)) return 3;
  if (t.play !== undefined) return 4;
  if (t.claim === 'hold') return 4.9;   // (69.79 step 4: the side's hold at a point it owns — an objective's, over the carry and the strikes)
  if (t.claim === 'carry') return 5;
  if (t.claim === 'objStrike') return 5.1;
  if (t.claim === 'hqStrike') return 5.2;
  if (t.claim === 'search') return 5.3;
  return 6;
}
/** 304 rank 3: what a named errand is — a supply run's leg, a gun's crew kept up there, a build, a beacon, a fire mission. */
function errandOrder(o: SquadOrder): boolean {
  return (o.kind === 'ride' && (!!o.convoy || o.keep === 'crew')) || o.kind === 'build' || o.kind === 'beacon' || o.kind === 'fireMission';
}
/** 304: what holds a rank, for a refusal's words. */
const RANK_NAMES = ['a direct order', "a human's order", 'its lift', 'an errand', 'a play', 'an objective', "the commander's plan"];
function rankName(r: number): string { return RANK_NAMES[r > 4 && r < 5 ? 5 : Math.floor(r)] ?? 'an order'; }   // (69.79 step 4: the hold's 4.9 an objective's)
/** 304: a task still standing at `tick` — not past its expiry, not a lapsed form, not a mark (a mark is knowledge, never a claim). */
function standing(state: OrderReadState, t: SquadTask, tick: number): boolean {
  return t.order.kind !== 'mark' && !(t.expires !== undefined && tick >= t.expires) && !orderFormLapsed(state, t.order);
}

/**
 * 69.72 step 5 (304): the rank a squad stands claimed at against another's squad-wide task — its best standing squad-wide
 * task's, a human's standing base (1: 153 (2), the bot commander orders only squads no human orders), else none (Infinity).
 * With `members`, its members' own tasks too (a planter's build, a driver's leg): what a planner picking a squad reads.
 */
export function squadClaimRank(state: OrderReadState, squadId: number, tick: number, members = false): number {
  const sq = state.squads?.find(s => s.id === squadId);
  if (!sq) return Infinity;
  let r = sq.base && sq.baseBy === 'human' ? 1 : Infinity;
  for (const t of sq.tasks) if ((members || !t.units) && standing(state, t, tick)) r = Math.min(r, taskRank(t));
  return r;
}
/** 304: the rank a unit stands claimed at against another's task naming it — its task in force's, else its squad's human base (1), else none. */
export function unitClaimRank(state: OrderReadState, unitId: number, tick: number): number {
  const t = squadTaskFor(state, unitId, tick);
  if (t) return taskRank(t);
  const sq = squadOf(state, unitId);
  return sq?.base && sq.baseBy === 'human' ? 1 : Infinity;
}

/**
 * 68.23 (decision 153): who gave the order in force for a unit — 'human', 'commander' (the team's bot commander), 'other'
 * (a brain, the harness, the Match), or null (no order). The same order `squadOrderFor` reads: its newest unexpired,
 * unlapsed task, else its squad's base.
 */
export function squadOrderSourceFor(state: OrderReadState, unitId: number, tick: number): 'human' | 'commander' | 'other' | null {
  const task = squadTaskFor(state, unitId, tick);
  if (task) return task.by === 'human' || task.by === 'commander' ? task.by : 'other';
  if (separatedAt(state, unitId, tick)) return null;
  const sq = squadOf(state, unitId);
  if (!sq?.base) return null;
  return sq.baseBy ?? 'other';
}

/** The task in force for a unit (or its whole squad): 304's highest rank, the newest within it, unexpired and unlapsed — or null. */
export function squadTaskFor(state: OrderReadState, unitId: number, tick: number): SquadTask | null {
  if (separatedAt(state, unitId, tick)) return null;   // 68.10: a separated member fights alone (no squad order reaches it)
  const sq = squadOf(state, unitId);
  if (!sq) return null;
  const out: SquadOrder[] = [];   // 63.1i: the dismounts that member has already obeyed
  // (69.4's open note, 69.42 (c): a supply run's driver and a build's planter kept their errand under a newer squad-wide task
  // of the commander's — 304 ranks it: a named errand (3) over a play, an objective or the commander's routine)
  let best: SquadTask | null = null, bestRank = Infinity;
  for (let i = sq.tasks.length - 1; i >= 0; i--) {
    const t = sq.tasks[i];
    if (t.expires !== undefined && tick >= t.expires) continue;
    if (t.units && !t.units.includes(unitId)) continue;
    if (t.order.kind === 'mark') continue;   // X2: a mark is knowledge — it never masks the unit's task (a withdrawal, a beacon, a tactic)
    if (t.order.kind === 'ride' && t.order.convoy?.leg === 'back') out.push(t.order);   // 69.4 step 5: a run's home leg ends its outward one, lapsed or not
    if (orderFormLapsed(state, t.order)) continue;   // 68.0: a dead target, a gone seat
    if (out.some(d => dismountSupersedes(d, t.order))) continue;   // 63.1i: told to get out of it since
    if (t.order.kind === 'dismount' && !seatedFor(state, unitId, t.order)) { out.push(t.order); continue; }   // out already
    const r = taskRank(t);
    if (r < bestRank) { best = t; bestRank = r; }   // (304: the highest rank in force; newest first, so a tie keeps the newer)
  }
  return best;
}

/**
 * 415 / 416: the skill a unit's squad serves now, and its phase — the leader's step's (`step`, LeadStep.skill: lane H's ambush
 * walk, lane C's skills), else the squad's task in force's (SquadTask.skill: the commander's ambush and rally). The one read
 * the callouts (ui/botCallouts.ts skillOf) and the squad card (core/commandTable.ts) share. Presentation: nothing in the sim
 * reads it.
 */
export function squadSkill(state: OrderReadState, unitId: number, tick: number, step?: { skill?: SkillTag }): SkillTag | undefined {
  return step?.skill ?? squadTaskFor(state, unitId, tick)?.skill;
}

export type OrderBody = Extract<MatchInputBody, { kind: 'order' }>;

export interface OrderApplied { state: GameState; squadIds: number[] }

/**
 * Apply an `order` input. A base order must address a squad; a task may address a squad or a list
 * of units (each unit's own squad takes it, filtered to those units). `CommandParams` (the turn
 * clock's leader commands) are not squad orders and are refused here; they keep today's path.
 * Returns the new state, or a refusal reason.
 */
/** X11: a refused call-in's log entry, for the Match to keep (an apply that refuses returns no state). */
export let refusalLog: GameState | null = null;
export function takeRefusalLog(): GameState | null { const s = refusalLog; refusalLog = null; return s; }

export function applyOrderInput(state: GameState, body: OrderBody, tick: number, ctx: { useWasm?: boolean } = {}): OrderApplied | { refused: string; code: string } {
  refusalLog = null;
  if ('commandType' in body.order) return { refused: 'Leader commands keep the command action for now', code: 'unsupported' };
  const order = body.order as SquadOrder;
  const squads = state.squads ?? [];
  const to = body.to;
  // 68.0: the order forms' own rules (a place that exists, a target the team knows, a seat that stands)
  const form = validateOrderForm(state, body, tick, ctx.useWasm);
  if (form) return form;
  // 66.4: a tactic is a task — it ends; it never becomes a posture
  if ((order.kind === 'bound' || order.kind === 'flank' || order.kind === 'withdraw') && body.tier !== 'task') return { refused: 'A tactic is a task, not a posture', code: 'tier' };
  // X2: a mark is team knowledge — a task, on a living planet
  if (order.kind === 'mark') {
    if (body.tier !== 'task') return { refused: 'A mark is a task, not a posture', code: 'tier' };
    if (!state.planets[order.planetIndex]?.alive) return { refused: `No planet ${order.planetIndex} to mark on`, code: 'planet' };
  }
  // X11: a call-in is answered now — a fire mission for a gun that can reach, or a refusal with the reason; both logged
  if (order.kind === 'callIn') {
    if (body.tier !== 'task') return { refused: 'A call-in is a task, not a posture', code: 'tier' };
    if (!('squad' in to)) return { refused: 'A call-in comes from a squad', code: 'squad' };
    const sq = squads.find(s => s.id === to.squad);
    if (!sq) return { refused: `No squad ${to.squad}`, code: 'squad' };
    if (sq.team !== body.team) return { refused: `Squad ${sq.id} is not team ${body.team}'s`, code: 'team' };
    const asker = state.players.find(p => sq.unitIds.includes(p.id) && p.alive);
    if (!asker) return { refused: `Nobody in squad ${sq.id} lives to call in`, code: 'callIn' };
    const blocked = callInBlocked(state, asker, order.planetIndex);
    const base = { turn: state.turnNumber, by: asker.id, team: sq.team, planetIndex: order.planetIndex, surfaceDir: order.surfaceDir };
    if (blocked) {
      // a refusal is state too: the log says why (the apply refuses, so the input log keeps the code)
      refusalLog = { ...state, callIns: [...(state.callIns ?? []), { ...base, answer: 'refused', detail: blocked }] };
      return { refused: blocked, code: 'callIn' };
    }
    const gun = gunFor(state, asker, order.planetIndex)!;
    const gunSquad = squadOf(state, gun.id);
    const mission: SquadTask = { order: { kind: 'fireMission', planetIndex: order.planetIndex, surfaceDir: order.surfaceDir, spread: CALL_IN_SPREAD }, units: [gun.id], issuedTick: tick, ...(body.by ? { by: body.by } : {}) };   // (304: tagged with who called it in)
    const detail = `${gun.name} answers from planet ${gun.planetIndex}`;
    return {
      state: {
        ...state,
        squads: gunSquad ? squads.map(s => (s.id === gunSquad.id ? { ...s, tasks: [...s.tasks, mission] } : s)) : squads,
        callIns: [...(state.callIns ?? []), { ...base, answer: 'fired', detail, gun: gun.id }],
      },
      squadIds: gunSquad ? [gunSquad.id] : [],
    };
  }
  // 125a: a beacon is planted by its carrier — a task, with the switch on, naming units that carry one, on a living planet
  if (order.kind === 'beacon') {
    if (body.tier !== 'task') return { refused: 'A beacon is a task, not a posture', code: 'tier' };
    if (!state.config.beacons) return { refused: 'This match has no beacons', code: 'beacon' };
    if (!state.planets[order.planetIndex]?.alive) return { refused: `No planet ${order.planetIndex} to plant on`, code: 'planet' };
    const named = 'units' in to ? to.units : (squads.find(s => s.id === to.squad)?.unitIds ?? []);
    if (!named.some(id => { const u = state.players.find(p => p.id === id); return !!u && u.alive && (u.beacons ?? 0) > 0; })) return { refused: 'Nobody named carries a beacon', code: 'beacon' };
    // 69.6f (decision 149): under Operations, only a sector behind the front that the side holds
    const site = spawnSiteRefusal(state, body.team, order);
    if (site) return { refused: site, code: 'sector' };
  }
  // 69.2b: a build is a task on the build path — its kind's switch on, someone named carrying one, ground that takes it
  if (order.kind === 'build') {
    if (body.tier !== 'task') return { refused: 'A build is a task, not a posture', code: 'tier' };
    if (!buildOn(state.config, order.structure)) return { refused: `This match has no ${order.structure}s`, code: 'build' };
    if (!state.planets[order.planetIndex]?.alive) return { refused: `No planet ${order.planetIndex} to build on`, code: 'planet' };
    const named = 'units' in to ? to.units : (squads.find(s => s.id === to.squad)?.unitIds ?? []);
    if (!named.some(id => { const u = state.players.find(p => p.id === id); return !!u && u.alive && buildsLeft(state, u, order.structure) > 0; })) return { refused: `Nobody named carries a ${order.structure}`, code: 'build' };
    const site = frontSiteKind(order.structure, state.config) ? spawnSiteRefusal(state, body.team, order) : null;   // 69.6f (69.4: a FOB too)
    if (site) return { refused: site, code: 'sector' };
    const ground = buildRefusal(state, order.structure, order, body.team);
    if (ground) return { refused: ground, code: 'ground' };
  }
  // §15.2: a fire mission is a task, at a point on a living planet
  if (order.kind === 'fireMission') {
    if (body.tier !== 'task') return { refused: 'A fire mission is a task, not a posture', code: 'tier' };
    if (!state.planets[order.planetIndex]?.alive) return { refused: `No planet ${order.planetIndex} to fire on`, code: 'planet' };
  }
  // 66.3: a formation is a squad's standing shape — base tier, on a squad, leaving its posture (base) as it is
  if (order.kind === 'formation') {
    if (body.tier !== 'base' || !('squad' in to)) return { refused: 'A formation is a base order on a squad', code: 'tier' };
    const sq = squads.find(s => s.id === to.squad);
    if (!sq) return { refused: `No squad ${to.squad}`, code: 'squad' };
    if (sq.team !== body.team) return { refused: `Squad ${sq.id} is not team ${body.team}'s`, code: 'team' };
    if (order.formation && order.formation.leader !== undefined && !sq.unitIds.includes(order.formation.leader)) return { refused: `Unit ${order.formation.leader} is not in squad ${sq.id}`, code: 'leader' };
    const next: MatchSquad = { ...sq };
    if (order.formation) next.formation = order.formation; else delete next.formation;
    return { state: { ...state, squads: squads.map(s => (s.id === sq.id ? next : s)) }, squadIds: [sq.id] };
  }
  if ('squad' in to) {
    const sq = squads.find(s => s.id === to.squad);
    if (!sq) return { refused: `No squad ${to.squad}`, code: 'squad' };
    if (sq.team !== body.team) return { refused: `Squad ${sq.id} is not team ${body.team}'s`, code: 'team' };
    // 68.23 (decision 153): who gave it is kept with it; a human's task clears the bot commander's base under it, so the
    // squad never falls back into an older plan of the bot's when the task lapses (the bot plans it afresh instead)
    const by = body.by ? { by: body.by } : {};
    // 68.27: a human's `free` base is a release ("As you were"): its orders and tasks cleared (out of any play), the bot
    // commander free to order it again; the squad reads released until its next order
    const release = body.tier === 'base' && order.kind === 'free' && body.by === 'human';
    const human = body.by === 'human';
    if (body.tier === 'base') {
      // 304 (153 (2)): another's posture never goes over a human's standing order — a human's base, or a human's task in force
      if (!human && ((sq.base && sq.baseBy === 'human') || sq.tasks.some(t => t.by === 'human' && !t.units && standing(state, t, tick)))) return { refused: `Squad ${sq.id} is held by a human's order`, code: 'claimed' };
      // 304: a posture replaces the commander's routine and its objectives (ranks 5–6: a strike or a carry stood down goes
      // with it) and keeps what outranks them — a lift, a named errand (69.42 (h)'s supply-run leg among them), a play; a
      // human's clears every task. (A mark goes with a new posture, as before.)
      const kept = human ? [] : sq.tasks.filter(t => t.order.kind !== 'mark' && taskRank(t) < 5 && standing(state, t, tick));
      const next = withReleased(withBaseBy({ ...sq, base: order.kind === 'free' ? null : order, tasks: kept }, release ? undefined : body.by), release);
      return { state: { ...state, squads: squads.map(s => (s.id === sq.id ? next : s)) }, squadIds: [sq.id] };
    }
    const add: SquadTask = { order, expires: body.expires, issuedTick: tick, ...by, ...(body.claim ? { claim: body.claim } : {}), ...(body.play !== undefined ? { play: body.play } : {}), ...(body.role ? { role: body.role } : {}), ...(body.skill ? { skill: body.skill } : {}) };
    let tasks = sq.tasks;
    if (order.kind !== 'mark') {
      // 304: one claim a squad — a task is refused where the squad stands claimed higher (the giver is told), and replaces the
      // older of its rank or lower, so when it lapses the squad falls back to its posture, never to an older task
      const r = taskRank(add);
      const held = squadClaimRank(state, sq.id, tick);
      if (held < r) return { refused: `Squad ${sq.id} is held for ${rankName(held)}`, code: 'claimed' };
      tasks = tasks.filter(t => t.order.kind === 'mark' || taskRank(t) < r);
    }
    const next = withReleased({ ...(human && sq.baseBy === 'commander' ? withBaseBy({ ...sq, base: null }, undefined) : sq), tasks: liveTasks([...tasks, add], tick) }, false);
    return { state: { ...state, squads: squads.map(s => (s.id === sq.id ? next : s)) }, squadIds: [sq.id] };
  }
  if (body.tier === 'base') return { refused: 'A base order belongs to a squad, not to units', code: 'tier' };
  const units = to.units;
  if (!units.length) return { refused: 'No units named', code: 'units' };
  const touched = new Map<number, number[]>();
  for (const u of units) {
    const sq = squadOf(state, u);
    if (!sq) return { refused: `Unit ${u} is in no squad`, code: 'squad' };
    if (sq.team !== body.team) return { refused: `Unit ${u} is not team ${body.team}'s`, code: 'team' };
    touched.set(sq.id, [...(touched.get(sq.id) ?? []), u]);
  }
  const task = (sqUnits: number[]): SquadTask => ({ order, units: sqUnits, expires: body.expires, issuedTick: tick, ...(body.by ? { by: body.by } : {}), ...(body.claim ? { claim: body.claim } : {}), ...(body.play !== undefined ? { play: body.play } : {}), ...(body.role ? { role: body.role } : {}), ...(body.skill ? { skill: body.skill } : {}) });
  // 304: a unit's own task — refused where a named unit stands claimed higher; it replaces that unit's older tasks of its rank or lower
  const r = order.kind === 'mark' ? Infinity : taskRank(task(units));
  if (order.kind !== 'mark') for (const u of units) {
    const held = unitClaimRank(state, u, tick);
    if (held < r) return { refused: `Unit ${u} is held for ${rankName(held)}`, code: 'claimed' };
  }
  const covered = (t: SquadTask, sqUnits: number[]) => t.order.kind !== 'mark' && !!t.units && t.units.every(u => sqUnits.includes(u)) && taskRank(t) >= r;
  return {
    state: { ...state, squads: squads.map(s => (touched.has(s.id) ? { ...s, tasks: liveTasks([...s.tasks.filter(t => !covered(t, touched.get(s.id)!)), task(touched.get(s.id)!)], tick) } : s)) },
    squadIds: [...touched.keys()].sort((a, b) => a - b),
  };
}

/**
 * 69.6c (lane C's Foothold diagnostic: mark tasks piled up on attacking squads, p90 8, at most 65, never the order in force):
 * a squad's tasks still worth keeping at `tick`, trimmed when a task is added to it — one past its `expires` is dropped; a
 * mark is kept past its own (the re-mark window) while the team's picture still reads it, its confidence over BELIEF_FLOOR
 * (about 111 s), as the picture would drop it anyway. (pruneTasks below drops marks at their expiry, so no tick calls it.)
 */
function liveTasks(tasks: readonly SquadTask[], tick: number): SquadTask[] {
  // (and a mark of an enemy the squad has marked again since: the picture keeps the newest of a unit's beliefs, so the older
  // never counts — 40-odd live marks on one squad were mostly the same few enemies seen over and over)
  const newest = new Map<number, number>();
  tasks.forEach((t, i) => { if (t.order.kind === 'mark' && t.order.unitId !== undefined) newest.set(t.order.unitId, i); });
  return tasks.filter((t, i) => t.order.kind === 'mark'
    ? markConfidence(t.issuedTick, tick) >= BELIEF_FLOOR && (t.order.unitId === undefined || newest.get(t.order.unitId) === i)
    : !(t.expires !== undefined && tick >= t.expires));
}

/**
 * 304's lift (69.72 step 5's follow-up): the units aboard a lift — a pickup's squad boarded (its `units`, a wave ship's or a
 * helicopter's before it lifts) and a helicopter lift's riders not yet out (69.74 step 1, core/heliLift.ts: its squad its
 * riders after the lift). Read from the flights, not core/dropship.ts's `dropshipAboard` (which counts the sorties' and the
 * pods' waiting too).
 */
export function liftAboard(state: Pick<GameState, 'dropships'>): Set<number> {
  const out = new Set<number>();
  for (const f of state.dropships?.flights ?? []) {
    for (const id of f.pickup?.units ?? []) out.add(id);
    if (f.lift) for (let i = f.out; i < f.riders.length; i++) out.add(f.riders[i]);
  }
  return out;
}

/**
 * Drop tasks that have expired or whose units are all dead (housekeeping; the state stays small) — and, 68.0, the
 * forms that have lapsed: an engage whose target is dead or whom the team no longer knows, a crew whose seat is gone;
 * a mark by its own rule (liveTasks: while the team's picture still reads it). 69.72 step 5 (DECISIONS 304): the Match
 * runs it every tick on the action clock (match.ts), so a stale task never holds a squad's claim. A lift's hold (304's rank 2)
 * outlasts its ship's lift while a member of its squad is aboard (`liftAboard`): from then it is `aboard`, its `expires`
 * gone, and it lapses when they are out — so nobody re-tasks the riders in flight.
 */
export function pruneTasks(state: GameState, tick: number, ctx: { useWasm?: boolean } = {}): GameState {
  if (!state.squads?.some(s => s.tasks.length)) return state;
  const alive = new Set(state.players.filter(p => p.alive).map(p => p.id));
  let aboard: Set<number> | null = null;
  let changed = false;
  const squads = state.squads.map(s => {
    const marks = new Set(liveTasks(s.tasks.filter(t => t.order.kind === 'mark'), tick));
    const kept = s.tasks.map(t => {
      if (t.claim !== 'lift' || !(t.aboard || (t.expires !== undefined && tick >= t.expires))) return t;
      aboard ??= liftAboard(state);
      if (!s.unitIds.some(id => alive.has(id) && aboard!.has(id))) return null;   // (out, or nobody boarded: it lapses)
      if (t.aboard) return t;
      const { expires: _lifted, ...held } = t;
      return { ...held, aboard: true as const };
    });
    const tasks = kept.filter((t): t is SquadTask => !!t && (t.order.kind === 'mark' ? marks.has(t) : !(t.expires !== undefined && tick >= t.expires) && !(t.units && !t.units.some(u => alive.has(u)))
      && !orderFormLapsed(state, t.order) && !(t.order.kind === 'engage' && !teamKnowsUnit(state, s.team, t.order.unitId, tick, ctx.useWasm))));
    if (tasks.length === s.tasks.length && tasks.every((t, i) => t === s.tasks[i])) return s;
    changed = true;
    return { ...s, tasks };
  });
  return changed ? { ...state, squads } : state;
}

/**
 * §15.2: the fire mission a unit's brain should fire on now — the newest one its squad holds for it (named, or the
 * whole squad's), or null.
 */
export function fireMissionFor(state: Pick<GameState, 'squads'>, unitId: number): Extract<SquadOrder, { kind: 'fireMission' }> | null {
  for (const sq of state.squads ?? []) {
    if (!sq.unitIds.includes(unitId)) continue;
    for (let i = sq.tasks.length - 1; i >= 0; i--) {
      const t = sq.tasks[i];
      if (t.order.kind !== 'fireMission') continue;
      if (t.units && !t.units.includes(unitId)) continue;
      return t.order;
    }
  }
  return null;
}

/** §15.2: a unit fired: its fire mission is done for it (a squad-wide mission keeps the members who haven't fired). */
export function completeFireMission(state: GameState, unitId: number): GameState {
  if (!fireMissionFor(state, unitId)) return state;
  const squads = (state.squads ?? []).map(sq => {
    if (!sq.unitIds.includes(unitId)) return sq;
    let done = false;
    const tasks: SquadTask[] = [];
    for (let i = sq.tasks.length - 1; i >= 0; i--) {
      const t = sq.tasks[i];
      if (!done && t.order.kind === 'fireMission' && (!t.units || t.units.includes(unitId))) {
        done = true;
        const left = (t.units ?? sq.unitIds).filter(u => u !== unitId);
        if (left.length) tasks.unshift({ ...t, units: left });
        continue;
      }
      tasks.unshift(t);
    }
    return { ...sq, tasks };
  });
  return { ...state, squads };
}
